import { useCallback } from 'react'

import { cn } from '@adverity/ds-tokens/cn'
import type { Toast, useToaster } from 'react-hot-toast'

import { toastGutter } from './constants'
import { createToastContentId } from './utils'

type Props = Pick<ReturnType<typeof useToaster>['handlers'], 'calculateOffset' | 'updateHeight'> & {
    toast: Toast
}

export const ToastWrapper = ({ toast, calculateOffset, updateHeight }: Props) => {
    const offset = calculateOffset(toast, {
        defaultPosition: 'bottom-center',
        reverseOrder: false,
        gutter: toastGutter,
    })

    const ref = useCallback(
        (el: HTMLElement | null) => {
            if (el && !toast.height) {
                const height = el.getBoundingClientRect().height

                updateHeight(toast.id, height)
            }
        },
        [toast.height, toast.id, updateHeight],
    )

    return (
        <div
            key={toast.id}
            id={toast.id}
            ref={ref}
            /**
             * The `bottom` offset lives on the toast itself (not the wrapper) so toasts of different
             * heights all anchor to the same bottom edge. The `translateY` offset stacks toasts above
             * one another and is dynamic, so it stays in `style` while everything else is Tailwind.
             */
            // oxlint-disable-next-line @adverity/oxlint/prefer-classname-over-style -- dynamic stacking offset cannot be a static Tailwind class
            style={{ transform: `translateY(-${offset}px)` }}
            className={cn(
                'bottom-large animate-fade-in absolute transition duration-fast ease-in-out',
                toast.visible ? 'opacity-100' : 'opacity-0',
            )}
            {...toast.ariaProps}
            aria-labelledby={createToastContentId(toast)}
        >
            {typeof toast.message === 'function' ? toast.message(toast) : toast.message}
        </div>
    )
}

ToastWrapper.displayName = 'ToastWrapper'
